import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { HorizontalTitleListItem } from '@/components/timeline/types';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { ListTitleSR } from '@/components/sponsored-recommendations/types';
import type { SraEventAction } from '@/@types/graphql-types';


export default /*#__PURE__*/_defineComponent({
  __name: 'SRTitleListGraphql',
  props: {
    fullWidth: { type: Boolean },
    titles: null,
    sponsoredAd: null,
    additionalContexts: null,
    impressionTrackingLabel: null,
    impressionTrackingProperty: null,
    impressionTrackingCategory: null,
    showSponsoredAd: { type: Boolean }
  },
  emits: ["onPosterClick", "onPosterClickOut", "imageError"],
  setup(__props: any) {

const props = __props as {
	fullWidth: boolean;
	titles: ListTitleSR[];
	sponsoredAd: SponsoredAdFragment | undefined;
	additionalContexts?: SnowplowContext[];
	impressionTrackingLabel?: string;
	impressionTrackingProperty?: string;
	impressionTrackingCategory?: any;
	showSponsoredAd: boolean;
};





// Note(valerio.mazza): SSR special case we need to track the Event on created
if (process.server && props.titles?.length) {
	trackSimilarTitlesLoaded();
}

const posterOverrides = computed(() => {
	if (!props.sponsoredAd?.campaign) return null;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];
	return Object.fromEntries(overrides.map(({ nodeId: id, promotionalImageUrl: url }) => [id, url]));
});

function transformPosterProps(posterProps: PosterGraphqlProps, title: HorizontalTitleListItem): PosterGraphqlProps {
	const posterUrl = posterOverrides.value?.[title.id];
	if (posterUrl == null) return posterProps;

	return {
		...posterProps,
		sponsoredRecImages: {
			imageUrl: posterUrl,
			sourceSets: [
				{
					srcSet: posterUrl?.replace(/\.\w+$/, '.avif'),
					imageProp: {
						extension: '.avif',
						type: 'image/avif',
					},
					sourceProp: {
						viewport: 'desktop',
					},
					media: null,
				},
			],
		},
		title: {
			...title,
			content: {
				...title.content,
				posterUrl,
			},
		} as HorizontalTitleListItem,
	};
}

const transformedTitles = computed(() => {
	if (!props.sponsoredAd?.campaign) return props.titles;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];

	const mapTitleIdWithUrl = Object.fromEntries(
		overrides.map(({ nodeId: id, watchNowOffer }) => [id, watchNowOffer?.standardWebURL])
	);

	// If we have a overwriten watchNowOffer unique to the title, we want to
	// inject that into the title object, replacing content.watchNowOffer
	return props.titles.map(title => {
		if (mapTitleIdWithUrl[title.id]) {
			title.content.fullPath = mapTitleIdWithUrl[title.id] || title.content.fullPath;
		}

		return title;
	});
});

function trackSimilarTitlesLoaded() {
	const properties: TrackingProviderPropertiesInterface = { action: 'loaded', nonInteraction: true };
	if (props.impressionTrackingLabel) properties.label = props.impressionTrackingLabel;

	TrackingHelper.trackEvent(ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS, properties, props.additionalContexts);
}

function trackScroll(label: string) {
	const properties: TrackingProviderPropertiesInterface = { action: 'horizontal_scroller_clicked', label };
	if (props.impressionTrackingLabel) properties.property = props.impressionTrackingLabel;

	TrackingHelper.trackEvent('userinteraction', properties, props.additionalContexts);
}

return { __sfc: true,props, posterOverrides, transformPosterProps, transformedTitles, trackSimilarTitlesLoaded, trackScroll, PosterGraphql, HorizontalTitleListGraphql }
}

})